import React, { useState } from "react";
import CoraaAIDialog from "./CoraaAIDialog";

const steps = [
  {
    icon: "💬",
    title: "Describe Your Need",
    bg: "from-green-400 via-blue-400 to-violet-400",
    desc: "Just ask CORAA - e.g., ‘What can we do for World Environment Day under ₹500/head?’",
    initialPrompt: "What can we do for World Environment Day under ₹500/head?",
  },
  {
    icon: "✨",
    title: "Get Instant Options",
    bg: "from-blue-400 via-purple-400 to-pink-400",
    desc: "CORAA returns tailored, SDG-aligned events with transparent pricing.",
    initialPrompt:
      "Give me SDG13 opportunities with transparent pricing in tabulation.",
  },
  {
    icon: "🚀",
    title: "Book & Launch Instantly",
    bg: "from-violet-400 via-indigo-400 to-green-400",
    desc: "Go live with verified nonprofits, total pricing, and all support included.",
  },
];

const PlanCampaignSection: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState<string>("");

  return (
    <section
      className="py-20 bg-[#0d1927] text-white relative overflow-hidden"
      id="for-companies"
    >
      {/* AI mesh/circuit background motif */}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[url('/images/ai-mesh.svg')] bg-cover bg-center" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <button
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-blue-500 text-white text-xs px-4 py-1 rounded-full font-semibold shadow animate-bounce cursor-pointer hover:shadow-lg transition-shadow"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Powered by CORAA
          </button>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight text-white drop-shadow-xl">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-pink-500 to-red-500">
              Plan a Campaign in 3 AI-Powered Steps
            </span>
          </h2>
          <p className="max-w-2xl mx-auto text-lg">
            CORAA makes CSR effortless: discover, plan, and measure campaigns in
            minutes, not weeks.
          </p>
        </div>
        <div className="flex flex-col md:flex-row justify-center gap-8 mb-12">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={`flex-1 flex flex-col items-center rounded-2xl shadow-xl p-8 mx-2 bg-gradient-to-br ${
                step.bg
              } text-white relative overflow-hidden ${
                step.initialPrompt ? "cursor-pointer" : ""
              }`}
              style={{ minWidth: 260 }}
              onClick={() => {
                if (step.initialPrompt) {
                  setInitialPrompt(step.initialPrompt);
                  setDialogOpen(true);
                }
              }}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/30 mb-4 text-3xl shadow-lg">
                <span role="img" aria-label={step.title}>
                  {step.icon}
                </span>
              </div>
              <h3 className="text-xl font-bold mb-2 drop-shadow-lg">
                {step.title}
              </h3>
              <p className="text-base font-medium mb-2 drop-shadow-sm">
                {step.desc}
              </p>
              {idx < steps.length - 1 && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 md:block hidden text-5xl text-white/40">
                  →
                </span>
              )}
              {/* Sparkle effect */}
              <span className="absolute left-4 top-4 animate-pulse text-yellow-200 text-2xl">
                ✨
              </span>
              <span className="absolute right-4 bottom-4 animate-pulse text-blue-200 text-2xl">
                🌟
              </span>
            </div>
          ))}
        </div>
        <div className="text-center">
          <button
            type="button"
            className="inline-block bg-gradient-to-r from-green-500 to-blue-500 text-white px-10 py-4 rounded-xl font-bold text-lg shadow-lg hover:scale-105 transition-transform duration-200"
            onClick={() => {
              setInitialPrompt("");
              setDialogOpen(true);
            }}
          >
            Ask CORAA now
          </button>
        </div>
      </div>
      <CoraaAIDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setInitialPrompt("");
        }}
        initialInput={initialPrompt}
      />
    </section>
  );
};

export default PlanCampaignSection;
